import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './Navbar.module.css';
import AppContext from '../../context/AppContext';

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'About Us', to: '/about' },
  { label: 'Courses', to: '/courses' },
  { label: 'Admissions', to: '/admissions' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'Contact', to: '/contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [announce, setAnnounce] = useState(true);
  const [navTheme, setNavTheme] = useState('light');
  const { openEnquiry } = useContext(AppContext);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let ticking = false;
    const onScrollTheme = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const nav = document.getElementById('vinfly-nav-id');
          if (nav) nav.style.pointerEvents = 'none';

          const el = document.elementFromPoint(window.innerWidth / 2, 70);
          if (nav) nav.style.pointerEvents = 'auto';

          if (el) {
            let isDark = !!el.closest('[data-theme="dark"]');

            // Fallback for dark backgrounds directly styled
            if (!isDark) {
              let current = el;
              while (current && current !== document.documentElement) {
                if (current.className && typeof current.className === 'string') {
                  if (current.className.includes('Footer_footer') || current.className.includes('dark-card')) {
                    isDark = true; break;
                  }
                }
                const bg = window.getComputedStyle(current).backgroundColor;
                if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
                  const match = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                  if (match) {
                    const r = parseInt(match[1], 10);
                    const g = parseInt(match[2], 10);
                    const b = parseInt(match[3], 10);
                    // Standard luminance formula
                    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                    if (luminance < 0.4) isDark = true;
                  }
                  break;
                }
                current = current.parentElement;
              }
            }
            setNavTheme(isDark ? 'dark' : 'light');
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScrollTheme, { passive: true });
    setTimeout(onScrollTheme, 100);
    return () => window.removeEventListener('scroll', onScrollTheme);
  }, [location]);

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    if (!announce) {
      document.body.classList.add('no-announce');
    } else {
      document.body.classList.remove('no-announce');
    }
  }, [announce]);

  const isActive = (to) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <>
      {/* ── ANNOUNCEMENT BAR ── */}
      {announce && (
        <div className={styles.announce}>
          <span>✈&nbsp; Admissions Open 2026-27 — Limited Seats Available &nbsp;|&nbsp; <Link to="/admissions">Apply Today →</Link></span>
          <button className={styles.announceClose} onClick={() => setAnnounce(false)} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* ── NAVBAR ── */}
      <nav id="vinfly-nav-id" className={`${styles.navbar} ${scrolled ? styles.scrolled : ''} ${!announce ? styles.noAnnounce : ''} ${(navTheme === 'dark' && !scrolled) ? styles.themeDark : styles.themeLight}`}>
        <div className={styles.inner}>
          {/* Logo */}
          <Link to="/" className={styles.logo}>
            <img src="/images/logo.png" alt="Vin Fly Logo" className={styles.logoImg} />
            <div className={styles.logoText}>
              <span className={styles.logoTextTop}>VIN FLY</span>
              <span className={styles.logoTextBottom}>AVIATION ACADEMY</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <ul className={styles.navLinks}>
            {navLinks.map(l => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className={`${styles.navLink} ${isActive(l.to) ? styles.active : ''}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* CTA + Hamburger */}
          <div className={styles.navRight}>
            <span className={styles.navPhone} style={{ marginRight: '1rem', color: 'var(--text-main)', fontWeight: 500, display: 'none' }} id="nav-phone">📞 +91 9960 555 941</span>
            <a href="#!" onClick={(e) => { e.preventDefault(); openEnquiry(); }} className={styles.enrollBtn}>Apply Now</a>
            <button
              className={`${styles.hamburger} ${menuOpen ? styles.open : ''}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {/* ── MOBILE OVERLAY ── */}
      <div className={`${styles.mobileMenu} ${menuOpen ? styles.mobileOpen : ''}`}>
        <ul className={styles.mobileLinks}>
          {navLinks.map((l, i) => (
            <li key={l.to} style={{ animationDelay: `${i * 0.07}s` }}>
              <Link to={l.to} className={`${styles.mobileLink} ${isActive(l.to) ? styles.activeMobile : ''}`}>
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
        <div className={styles.mobileFooter}>
          <a href="tel:+919960555941" style={{ color: 'inherit', textDecoration: 'none' }}>+91 9960 555 941</a>
          <a href="tel:+919960555942" style={{ color: 'inherit', textDecoration: 'none' }}>+91 9960 555 942</a>
        </div>
      </div>
    </>
  );
};

export default Navbar;
